/**
 * SMS HTTP client — wraps `@tiaki/http-client` per ADR 113 §"Phase 2".
 *
 * Exposes:
 *   - `client` — the typed openapi-fetch client (used by every SMS service).
 *   - `clearStaleMasquerade` — vendor-org masquerade recovery helper.
 *     Wired into the lib's `afterResponse` chain (see below) so
 *     it fires automatically; also exported for explicit invocation by any
 *     code path that needs it.
 *   - `ApiValidationError` — re-exported from `@tiaki/http-client` so
 *     `instanceof` checks at form call sites continue to resolve.
 *   - `onTokenRefreshChange` / `onTokenRefreshComplete` — listener API
 *     re-exports for the SMS WebSocket service to subscribe to.
 *   - `DownloadFileOptions` / `DownloadResult` types for the download helper.
 *
 * The lib's `createClient` does the load-bearing work: 401-queue refresh,
 * `x-refreshed-token` rotation, stale-perms reactive refresh (ADR 100),
 * `Sec-GPC` propagation (ADR 099 §1), and `X-Vendor-Organization`
 * masquerade injection. SMS forms get the
 * `ApiValidationError` mapping for free via the lib's middleware.
 *
 * Every SMS service file consumes `client` directly; there is no
 * `apiClient` adapter.
 *
 * The `X-Vendor-Masquerade-Stale: 1` recovery is wired
 * through the lib's `afterResponse` extension point so it fires on
 * every SMS request — typed `client.GET/POST/...`, escape-hatch
 * `client.ky.*`, and downloads — without consumers having to opt in.
 */
import {
  createClient,
  ApiValidationError,
  onTokenRefreshChange,
  onTokenRefreshComplete,
  type DownloadFileOptions,
  type DownloadResult,
  type HttpClient,
} from '@tiaki/http-client';
import type { paths } from '@tiaki/http-client/types';
import { StorageKeys, httpStorageFromAdapter } from '@aegix/auth-core';
import { smsStorage, SMS_LEGACY_STORAGE_KEYS } from './sms-auth';
import { getRedirectUrl, publishRefreshRedirect } from './brandRedirect';
import { deviceIdBeforeRequest } from './deviceIdHeader';
import i18n from '../i18n/config';

export { ApiValidationError, onTokenRefreshChange, onTokenRefreshComplete };
export type { DownloadFileOptions, DownloadResult };

const STALE_MASQUERADE_HEADER = 'x-vendor-masquerade-stale';

/**
 * Clear the cached vendor masquerade and reset the page.
 *
 * Called when the backend stamps `X-Vendor-Masquerade-Stale: 1` on a
 * response — the active org has flipped out of `onboard` (or the
 * vendor is unlinked from it) since the dropdown last
 * refreshed, so the `X-Vendor-Organization` header we just sent was
 * rejected. Clearing the cached active-org stops sending the stale
 * header on subsequent requests and `reload()` forces AuthContext to
 * remount with a clean state.
 *
 * Clears BOTH the canonical slot
 * (`aegix.auth.v1.active_org` via the adapter) AND the legacy slot
 * (`aegix_sms_active_org` directly on localStorage). The legacy
 * direct-clear is load-bearing: leaving the legacy entry behind
 * would let the adapter's `legacyKeys` capability re-promote the
 * stale value to canonical on the next read, defeating the clear.
 */
export function clearStaleMasquerade(): void {
  try {
    smsStorage.remove(StorageKeys.activeOrganization);
  } catch {
    // adapter unavailable (private mode, SSR) — nothing to clear.
  }
  try {
    localStorage.removeItem(SMS_LEGACY_STORAGE_KEYS.activeOrganization);
  } catch {
    // localStorage unavailable — already covered above.
  }
  if (
    typeof window !== 'undefined' &&
    typeof window.location?.reload === 'function'
  ) {
    window.location.reload();
  }
}

const baseURL =
  (typeof window !== 'undefined' ? window.__ENV?.NX_API_URL : '') ?? '';

/**
 * Reentrancy guard for the masquerade-stale hook. `clearStaleMasquerade()`
 * triggers a full-page reload, so the hook's downstream effect is to tear
 * down the entire JS context — but the reload is async (browsers settle
 * pending tasks first). Without this guard, multiple in-flight requests
 * arriving with the stale header would each invoke `localStorage.removeItem`
 * + `window.location.reload()`, which is harmless on real browsers but
 * generates noisy double-fires in tests. The guard is per-page-load and
 * never reset — once we've decided to reload, every later observation is a
 * no-op.
 */
let masqueradeStaleFired = false;

/**
 * The shared typed client — every SMS service file imports `{ client }`
 * from this module and calls `client.GET/POST/PATCH/DELETE` directly.
 */
export const client: HttpClient<paths> = createClient<paths>({
  baseURL,
  // One storage source of truth — the same `smsStorage` adapter
  // backs `<AuthProvider storage>` (App.tsx) and this client, via the
  // shared `httpStorageFromAdapter` helper every app uses. Reading the
  // token straight from `localStorage` here would bypass the adapter's
  // `__cookie__` scrub and send `Authorization: JWT __cookie__` → 401 →
  // logout.
  storage: httpStorageFromAdapter(smsStorage),
  refresh: async (refreshToken: string) => {
    try {
      const res = await fetch(`${baseURL}/api/v2/api-token-refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: refreshToken }),
      });
      if (!res.ok) return { token: null };
      const body = (await res.json()) as {
        token?: string;
        redirect_url?: string;
      };
      // ADR 115 Phase 4 — a refresh response may carry an
      // optional `redirect_url` when the current host does not match
      // the tenant's brand-correct host (e.g. a mid-session brand flip).
      // Publish it to the BrandRedirectGate, which surfaces a graceful
      // interstitial rather than auto-navigating — refreshes happen during
      // active work and must not yank the user off their screen. The lib's
      // refresh contract only consumes `token`, so the redirect rides this
      // app-local channel. Optional/undefined-safe: a backend that omits
      // the field makes this a no-op.
      const redirectUrl = getRedirectUrl(body);
      if (redirectUrl) {
        publishRefreshRedirect(redirectUrl);
      }
      return { token: body.token ?? null };
    } catch {
      return { token: null };
    }
  },
  onAuthFailure: () => {
    if (
      typeof window !== 'undefined' &&
      window.location.pathname !== '/login'
    ) {
      window.location.href = '/login';
    }
  },
  vendorOrgAccessor: () => {
    try {
      // Route through the SMS storage adapter so we read
      // canonical `aegix.auth.v1.active_org` AND let the adapter's
      // `legacyKeys` capability promote a pre-canonical
      // `aegix_sms_active_org` value on first access. No more direct
      // `localStorage.getItem` here.
      const org = smsStorage.get<{ id?: string | number } | null>(
        StorageKeys.activeOrganization,
      );
      return org?.id ?? null;
    } catch {
      return null;
    }
  },
  // Any response carrying `X-Vendor-Masquerade-Stale: 1` triggers
  // a one-shot localStorage purge + page reload. The hook returns `undefined`
  // so ky propagates the original response to the caller — `clearStaleMasquerade()`
  // tears the page down, which terminates whatever Promise was about to
  // resolve to the now-rejected masqueraded data.
  afterResponse: [
    ({ response }) => {
      if (
        !masqueradeStaleFired &&
        response.headers.get(STALE_MASQUERADE_HEADER) === '1'
      ) {
        masqueradeStaleFired = true;
        clearStaleMasquerade();
      }
      return undefined;
    },
  ],
});

// Dynamically inject Accept-Language matching user's active locale preference
client.use({
  onRequest({ request }) {
    const lang = i18n.language || 'en';
    request.headers.set('Accept-Language', lang.split('-')[0]);
    return request;
  },
});

// Stamp the persisted device id on the raw-ky
// escape hatch (`client.ky.*` — bulk-invite uploads, sp-metadata fetches,
// schema-gap POSTs) so device-scoped session enforcement sees
// SMS traffic. The typed openapi-fetch surface is left alone ON PURPOSE —
// `deleteDevice` (`client.DELETE('/api/v2/fcm-token/{id}/')`,
// `services/users.service.ts`) would sweep the admin's own browser
// registration if the header rode along; see `deviceIdHeader.ts` for the
// full scope note. Login/logout/SSO (raw-fetch adapter surfaces) are
// covered by `deviceIdFetch` in `sms-auth.ts` and `pages/auth/Login.tsx`.
client.ky = client.ky.extend({
  hooks: { beforeRequest: [deviceIdBeforeRequest] },
});

/**
 * Test hook — reset the masquerade-stale guard so a fresh test gets a clean
 * recovery path. Not part of the public surface.
 *
 * @internal
 */
export function _resetMasqueradeStaleGuardForTests(): void {
  masqueradeStaleFired = false;
}
